import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { App, Avatar, Button, Dropdown, Input, Popover, Tag } from 'antd';

import { DataState, Skeleton, deriveDataState } from '../loading';
import MarkdownContent from '../common/MarkdownContent';
import UserAvatar from '../users/UserAvatar';
import { userLabel } from '../users/UserChip';
import { buildActionMenu } from '../common/actionMenu';
import { confirmDestructive } from '../common/confirmDestructive';
import { CHEVRON_DOWN, CHEVRON_RIGHT, DOCUMENT, KEBAB } from '../icons';
import useItemComments from '../../hooks/useItemComments';
import { COMMENT_MAX_LENGTH, commentRefusal } from '../../utils/commentRefusals';
import { getItemComment, getItemCommentHistory } from '../../services/itemComments';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { formatRelativeToNow } from '../../utils/formatters';
import { normalizeId } from '../../utils/ids';

const { TextArea } = Input;

/** How often the "3 minutes ago" words are re-read while the section is open. */
const CLOCK_MS = 30000;
/** Under this many seconds old, or any time ahead of this device's clock, a comment is "just now". */
const JUST_NOW_SECONDS = 45;
/** About how long a phone's keyboard takes to rise, before the box is scrolled into the room left. */
const KEYBOARD_MS = 350;
/** A counter shows once a comment is this close to the limit. */
const COUNT_FROM = Math.floor(COMMENT_MAX_LENGTH * 0.9);

/**
 * When a comment was written, in words: "just now" until it is a little old
 * (and for a time ahead of this device's clock, which a server a few seconds
 * fast would otherwise print as "a few seconds from now"), then the relative
 * reading every other date on the platform uses. The exact time is the
 * `title` of whatever shows this.
 */
export function commentAge(value, now = Date.now()) {
	if (!value) return '';
	const at = new Date(value).getTime();
	if (!Number.isFinite(at)) return '';
	if (now - at < JUST_NOW_SECONDS * 1000) return 'just now';
	return formatRelativeToNow(value);
}

/** What a delete does, under the strict-auditing mode it will be made in. */
export const deleteEffectWords = strict => (strict
	? 'It stays in the list as a Deleted comment, and while strict comment auditing is on, anyone who can read this item can open its text.'
	: 'It leaves the list and cannot be brought back. Its text is not shown to anyone, though keepr keeps a record of it.');

const AUDIT_CHANGED_WORDS = 'Strict comment auditing was changed for this card since you opened this item, so nothing was deleted yet. Here is what deleting does now.';

const authorName = user => (user ? userLabel(user) : 'Someone');

/** What a note from before comments (KPR-304's `migrated`) says in place of its author. */
export const MIGRATED_LABEL = 'Note from before comments';

/** A note from before comments: the API marks it `migrated: { from, at }`. */
export const isMigratedComment = comment => Boolean(comment?.migrated && typeof comment.migrated === 'object');

/**
 * When a comment is dated on its row: when it was written, or for a note from
 * before comments, when it became a comment (`migrated.at`).
 */
const shownAt = comment => (isMigratedComment(comment) && comment.migrated.at) || comment.createdAt;

/**
 * Whether the person may edit / delete this comment: the API's `canEdit` /
 * `canDelete` (KPR-801). An answer from an API older than those flags falls
 * back to its own rule then: your own comment, while you may write.
 */
export function commentRights(comment, canComment) {
	const legacy = comment?.mine === true && canComment === true && !isMigratedComment(comment);
	return {
		canEdit: typeof comment?.canEdit === 'boolean' ? comment.canEdit && !isMigratedComment(comment) : legacy,
		canDelete: typeof comment?.canDelete === 'boolean' ? comment.canDelete : legacy,
	};
}

/** The mark beside a note from before comments: no person, a document. */
const MigratedMark = ({ size = 28 }) => (
	<Avatar size={size} icon={<DOCUMENT />} className="item-comment__migrated-mark" aria-hidden="true" />
);

/** Re-render on a slow clock so "just now" does not stay "just now". */
function useClock() {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), CLOCK_MS);
		return () => clearInterval(timer);
	}, []);
	return now;
}

/**
 * The text box for a new comment, and for editing your own: Post / Save
 * beside Cancel (an edit only). ⌘↵ sends it; Esc cancels an edit, or clears
 * what you typed. Both stop at the box, so they never reach the item form's
 * own ⌘↵ Save and Esc Cancel around it.
 */
function CommentTextBox({
	initial = '',
	editing = false,
	busy = false,
	autoFocus = false,
	onSubmit,
	onCancel,
	label,
	submitLabel,
	hintVerb,
}) {
	const [text, setText] = useState(initial);
	const [problem, setProblem] = useState('');
	const ref = useRef(null);
	const boxRef = useRef(null);
	const hintId = useId().replace(/:/g, '');

	useEffect(() => {
		if (!autoFocus) return;
		const el = ref.current?.resizableTextArea?.textArea;
		if (!el) return;
		el.focus();
		// Cursor at the end of what is already there, not before it.
		const end = el.value.length;
		try { el.setSelectionRange(end, end); } catch (e) { /* not every input type takes a range */ }
	}, [autoFocus]);

	const trimmed = text.trim();
	const unchanged = editing && trimmed === initial.trim();
	const canSend = trimmed.length > 0 && !unchanged && !busy;

	const submit = useCallback(async () => {
		if (busy) return;
		if (editing && unchanged) { onCancel?.(); return; }
		if (!trimmed) return;
		setProblem('');
		const result = await onSubmit(text);
		if (result?.ok) {
			if (!editing) setText('');
		} else if (result?.text) {
			// What was typed stays, with the reason beside it.
			setProblem(result.text);
		}
	}, [busy, editing, unchanged, trimmed, text, onSubmit, onCancel]);

	// On a phone the keyboard rises over the lower part of the page: once it
	// is up, bring the whole box (its Post button too) to the foot of what is
	// left in view. The box stays in the page's flow — no fixed bar to fight
	// the keyboard.
	const handleFocus = () => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
		if (!window.matchMedia('(pointer: coarse)').matches) return;
		setTimeout(() => boxRef.current?.scrollIntoView?.({ block: 'end', behavior: 'smooth' }), KEYBOARD_MS);
	};

	const handleKeyDown = event => {
		if (event.isComposing || event.keyCode === 229) return;
		if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
			event.preventDefault();
			event.stopPropagation();
			submit();
		} else if (event.key === 'Escape') {
			if (editing) {
				event.preventDefault();
				event.stopPropagation();
				onCancel?.();
			} else if (text) {
				// A comment half-written: Esc drops it, a second Esc leaves.
				event.preventDefault();
				event.stopPropagation();
				setText('');
				setProblem('');
			}
		}
	};

	return (
		<div ref={boxRef} className={`item-comment-box${editing ? ' item-comment-box--editing' : ''}`}>
			<TextArea
				ref={ref}
				value={text}
				onChange={event => { setText(event.target.value); if (problem) setProblem(''); }}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				maxLength={COMMENT_MAX_LENGTH}
				autoSize={{ minRows: editing ? 2 : 3, maxRows: 12 }}
				placeholder={editing ? undefined : 'Add a comment'}
				aria-label={label}
				aria-describedby={hintId}
				disabled={busy}
				className="item-comment-box__input"
			/>
			<div className="item-comment-box__bar">
				<span id={hintId} className="item-comment-box__hint">
					{text.length >= COUNT_FROM
						? `${text.length.toLocaleString()} of ${COMMENT_MAX_LENGTH.toLocaleString()} characters`
						: 'Markdown works.'}
					<span className="item-comment-box__keys"> <kbd>⌘↵</kbd> {hintVerb || submitLabel}{editing ? <>, <kbd>Esc</kbd> Cancel</> : null}</span>
				</span>
				<span className="item-comment-box__actions">
					{editing ? (
						<Button className="item-comment-box__cancel" onClick={() => onCancel?.()} disabled={busy}>Cancel</Button>
					) : null}
					<Button type="primary" className="item-comment-box__send" onClick={submit} disabled={!canSend} loading={busy}>
						{submitLabel}
					</Button>
				</span>
			</div>
			{problem ? <div className="item-comment-box__problem" role="alert">{problem}</div> : null}
		</div>
	);
}

/** An edited comment's earlier texts, oldest first, each with its time. */
function CommentHistory({ itemId, commentId }) {
	const [state, setState] = useState({ status: 'loading', versions: [], error: null });
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setState(prev => ({ ...prev, status: prev.versions.length ? 'ready' : 'loading', error: null }));
		getItemCommentHistory(itemId, commentId)
			.then(({ versions }) => { if (!cancelled) setState({ status: 'ready', versions, error: null }); })
			.catch(error => {
				if (!cancelled) setState(prev => ({ ...prev, status: 'error', error: commentRefusal(error, 'history').text }));
			});
		return () => { cancelled = true; };
	}, [itemId, commentId, attempt]);

	return (
		<DataState
			state={deriveDataState({
				loading: state.status === 'loading',
				error: state.status === 'error' ? state.error : null,
				hasData: state.versions.length > 0,
				isEmpty: state.status === 'ready' && state.versions.length === 0,
			})}
			hasData={state.versions.length > 0}
			skeleton={<Skeleton variant="line" count={3} />}
			error={state.error}
			onRetry={() => setAttempt(n => n + 1)}
			label="the history"
			empty={<div className="item-comment-history__empty">No earlier versions.</div>}
			isEmpty={state.status === 'ready' && state.versions.length === 0}
		>
			<ol className="item-comment-history" aria-label="Versions of this comment, oldest first">
				{state.versions.map((version, index) => (
					<li key={`${version.at}-${index}`} className="item-comment-history__version">
						<div className="item-comment-history__when">
							<time dateTime={version.at} title={formatDisplayDateTime(version.at)}>{formatDisplayDateTime(version.at)}</time>
							{version.current ? <Tag className="item-comment-history__current">Current</Tag> : null}
						</div>
						<MarkdownContent markdown={version.body} className="item-comment-history__body" />
					</li>
				))}
			</ol>
		</DataState>
	);
}

/** The *edited* marker: pressed, it opens the comment's history. */
function EditedMarker({ itemId, commentId, editedAt }) {
	return (
		<Popover
			trigger="click"
			placement="bottomLeft"
			destroyTooltipOnHide
			overlayClassName="item-comment-history-pop"
			title="Earlier versions"
			content={<CommentHistory itemId={itemId} commentId={commentId} />}
		>
			<button
				type="button"
				className="item-comment__edited"
				title={editedAt ? `Edited ${formatDisplayDateTime(editedAt)}` : 'Edited'}
				aria-haspopup="dialog"
			>
				edited
			</button>
		</Popover>
	);
}

/** A deleted comment under strict auditing: who, when, and its last text on request. */
function DeletedRow({ comment, itemId, now }) {
	const [open, setOpen] = useState(false);
	const [text, setText] = useState({ status: 'idle', body: '', error: null });
	const bodyId = useId().replace(/:/g, '');

	const load = useCallback(() => {
		setText(prev => ({ ...prev, status: 'loading', error: null }));
		getItemComment(itemId, normalizeId(comment._id))
			.then(full => setText({ status: 'ready', body: typeof full?.body === 'string' ? full.body : '', error: null }))
			.catch(error => setText({ status: 'error', body: '', error: commentRefusal(error, 'read').text }));
	}, [itemId, comment._id]);

	const toggle = () => {
		const next = !open;
		setOpen(next);
		if (next && text.status === 'idle') load();
	};

	const deleter = comment.deletedBy || null;
	const when = comment.deletedAt;
	const deletedBy = deleter ? authorName(deleter) : null;

	return (
		<li className="item-comment item-comment--deleted" data-comment-id={normalizeId(comment._id)} data-deleted="true">
			<div className="item-comment__avatar">
				{isMigratedComment(comment) && !deleter
					? <MigratedMark />
					: <UserAvatar user={comment.author || deleter || { fullName: '' }} size={28} />}
			</div>
			<div className="item-comment__main">
				<div className="item-comment__head">
					<span className="item-comment__deleted-label">Deleted comment</span>
					<span className="item-comment__meta">
						{deletedBy ? `${deletedBy} deleted it ` : 'Deleted '}
						<time dateTime={when} title={formatDisplayDateTime(when)}>{commentAge(when, now)}</time>
					</span>
					<button
						type="button"
						className="item-comment__reveal"
						aria-expanded={open}
						aria-controls={open ? bodyId : undefined}
						onClick={toggle}
					>
						{open ? <CHEVRON_DOWN aria-hidden="true" /> : <CHEVRON_RIGHT aria-hidden="true" />}
						<span>{open ? 'Hide text' : 'Show text'}</span>
					</button>
				</div>
				{open ? (
					<div id={bodyId} className="item-comment__deleted-text">
						{text.status === 'loading' || text.status === 'idle' ? <Skeleton variant="line" count={2} /> : null}
						{text.status === 'error' ? (
							<div role="alert">{text.error} <button type="button" className="item-comment__reveal" onClick={load}>Retry</button></div>
						) : null}
						{text.status === 'ready' ? (
							<>
								<div className="item-comment__written">
									{isMigratedComment(comment) ? `${MIGRATED_LABEL}, made a comment ` : `Written by ${authorName(comment.author)}, `}
									<time dateTime={shownAt(comment)} title={formatDisplayDateTime(shownAt(comment))}>{commentAge(shownAt(comment), now)}</time>
								</div>
								<MarkdownContent markdown={text.body} className="item-comment__body" />
							</>
						) : null}
					</div>
				) : null}
			</div>
		</li>
	);
}

/**
 * One live comment: who, when, the text, and Edit / Delete when the API says
 * the person may. A note from before comments says so in place of its
 * author, dated when it became a comment; nobody edits it, and Delete shows
 * only for a manager (the API's `canDelete`).
 */
function CommentRow({ comment, itemId, canEdit, canDelete, strictAuditing, now, edit, remove, busy }) {
	const { modal, message } = App.useApp();
	const [editing, setEditing] = useState(false);
	const commentId = normalizeId(comment._id);
	const menuRef = useRef(null);
	const rowBusy = busy && busy.id === commentId;

	// Asks, then deletes under the mode the person was shown. When the card's
	// mode changed meanwhile the API refuses (nothing deleted); the list reads
	// again and this asks once more, in the words of the mode that is true now.
	const askDelete = useCallback((mode = strictAuditing, changed = false) => {
		confirmDestructive(modal, {
			title: 'Delete this comment?',
			content: (
				<>
					{changed ? <p className="item-comment__confirm-changed">{AUDIT_CHANGED_WORDS}</p> : null}
					<p className="item-comment__confirm-effect">{deleteEffectWords(mode)}</p>
				</>
			),
			okText: 'Delete comment',
			onOk: async () => {
				const result = await remove(commentId);
				if (result.ok) return;
				if (result.kind === 'audit_changed' && typeof result.strictAuditing === 'boolean') {
					askDelete(result.strictAuditing, true);
				} else if (result.text && !result.quiet) {
					message.error(result.text);
				}
			},
		});
	}, [modal, message, remove, commentId, strictAuditing]);

	const menu = useMemo(() => {
		const actions = [
			canEdit ? { key: 'edit', label: 'Edit comment', group: 'record', glyph: 'edit', onClick: () => setEditing(true) } : null,
			canDelete ? { key: 'delete', label: 'Delete comment…', group: 'destructive', glyph: 'delete', onClick: () => askDelete() } : null,
		].filter(Boolean);
		return actions.length ? buildActionMenu(actions, { showGroupLabels: false }) : null;
	}, [canEdit, canDelete, askDelete]);

	const closeEdit = () => {
		setEditing(false);
		// Back to where the person was: the row's own menu.
		setTimeout(() => menuRef.current?.focus?.(), 0);
	};

	const author = comment.author || null;
	const migrated = isMigratedComment(comment);
	const created = shownAt(comment);

	return (
		<li
			className={`item-comment${migrated ? ' item-comment--migrated' : ''}`}
			data-comment-id={commentId}
			data-mine={comment.mine ? 'true' : 'false'}
			data-migrated={migrated ? 'true' : undefined}
		>
			<div className="item-comment__avatar">
				{migrated ? <MigratedMark /> : <UserAvatar user={author || { fullName: '' }} size={28} />}
			</div>
			<div className="item-comment__main">
				<div className="item-comment__head">
					<span className="item-comment__author">{migrated ? MIGRATED_LABEL : authorName(author)}</span>
					<span className="item-comment__meta">
						<time dateTime={created} title={formatDisplayDateTime(created)}>{commentAge(created, now)}</time>
						{comment.edited || comment.editedAt ? (
							<>
								{' · '}
								<EditedMarker itemId={itemId} commentId={commentId} editedAt={comment.editedAt} />
							</>
						) : null}
					</span>
					{menu && !editing ? (
						<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
							<Button
								ref={menuRef}
								type="text"
								className="item-comment__menu"
								icon={<KEBAB />}
								aria-label="Comment actions"
								disabled={rowBusy}
							/>
						</Dropdown>
					) : null}
				</div>
				{editing ? (
					<CommentTextBox
						editing
						autoFocus
						initial={typeof comment.body === 'string' ? comment.body : ''}
						busy={Boolean(rowBusy)}
						label="Edit your comment"
						submitLabel="Save"
						onCancel={closeEdit}
						onSubmit={async body => {
							const result = await edit(commentId, body);
							if (result.ok) closeEdit();
							else if (result.kind === 'refresh') setEditing(false);
							return result;
						}}
					/>
				) : (
					<MarkdownContent markdown={comment.body} className="item-comment__body" />
				)}
			</div>
		</li>
	);
}

/**
 * COMMENTS (KPE-005, KPR-307): the journal that replaces a card's notes
 * section, on the details and the item form. The parent decides whether the
 * card allows comments and renders this only then; everything else comes from
 * the API (hooks/useItemComments.js): who may write (the composer), whether
 * deleted comments leave a row (strict auditing) and which comments the
 * person may edit or delete (each comment's `canEdit` / `canDelete`; KPR-801:
 * a note from before comments, which a manager may delete and nobody edits).
 *
 *   variant="read"  the details: a heading, then the list and the box
 *   variant="form"  the item form: the heading is a disclosure, folded unless
 *                   the card opens it (`defaultOpen`) or the item has comments
 *
 * Draws nothing when the item cannot be read or comments are off, and
 * nothing for a reader of an item with none: there is nothing to read and
 * nothing they may add.
 */
export default function ItemComments({
	itemId,
	enabled = true,
	variant = 'read',
	defaultOpen = false,
	refreshKey = 0,
	className = '',
}) {
	const saved = Boolean(normalizeId(itemId)) && normalizeId(itemId) !== '-1';
	const comments = useItemComments({ itemId, enabled: enabled && saved });
	const now = useClock();
	const [override, setOverride] = useState(null);
	const bodyId = useId().replace(/:/g, '');

	// A section the details view keeps mounted across a refresh reads again.
	const firstKey = useRef(refreshKey);
	useEffect(() => {
		if (firstKey.current === refreshKey) return;
		firstKey.current = refreshKey;
		comments.reload();
	}, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

	const isForm = variant === 'form';
	const classes = ['item-comments', `item-comments--${variant}`, className].filter(Boolean).join(' ');

	// An item not yet saved has nothing to comment on: say when it will.
	if (!saved) {
		return (
			<section className={classes} data-testid="item-comments">
				<div className={`micro-label item-comments__kicker${isForm ? ' form-panel__kicker' : ''}`}>Comments</div>
				<p className="item-comments__note">Comments open once this item is saved.</p>
			</section>
		);
	}

	if (!enabled || comments.status === 'idle' || comments.status === 'hidden') return null;
	if (comments.loaded && comments.status !== 'error' && !comments.canComment && comments.comments.length === 0) return null;

	const ready = comments.loaded;
	const open = !isForm || (override === null ? (defaultOpen || comments.comments.length > 0) : override);
	const count = ready && comments.total > 0 ? comments.total : null;
	const remaining = Math.max(0, comments.total - comments.comments.length);

	const heading = isForm ? (
		<button
			type="button"
			className="form-panel__kicker-toggle item-comments__toggle"
			aria-expanded={open}
			aria-controls={open ? bodyId : undefined}
			onClick={() => setOverride(!open)}
		>
			{/* One icon, turned a quarter when the section opens (form-layout.css). */}
			<CHEVRON_RIGHT className="form-panel__kicker-chevron" aria-hidden="true" />
			<span className="micro-label form-panel__kicker">Comments</span>
			{count ? <span className="form-panel__count">{count.toLocaleString()}</span> : null}
		</button>
	) : (
		<div className="item-comments__heading">
			<span className="micro-label item-comments__kicker">Comments</span>
			{count ? <span className="item-comments__count">{count.toLocaleString()}</span> : null}
		</div>
	);

	return (
		<section className={classes} data-testid="item-comments" aria-label="Comments">
			<DataState
				state={deriveDataState({
					loading: comments.status === 'loading',
					error: comments.status === 'error' ? comments.error : null,
					hasData: ready,
				})}
				hasData={ready}
				skeleton={<Skeleton variant="list-row" count={2} />}
				error={comments.error}
				onRetry={comments.reload}
				label="comments"
			>
				{heading}
				{open ? (
					<div id={bodyId} className="item-comments__body">
						{comments.comments.length ? (
							<ol className="item-comments__list">
								{comments.comments.map(comment => (comment.deleted === true ? (
									<DeletedRow key={normalizeId(comment._id)} comment={comment} itemId={normalizeId(itemId)} now={now} />
								) : (
									<CommentRow
										key={normalizeId(comment._id)}
										comment={comment}
										itemId={normalizeId(itemId)}
										{...commentRights(comment, comments.canComment)}
										strictAuditing={comments.strictAuditing}
										now={now}
										edit={comments.edit}
										remove={comments.remove}
										busy={comments.busy}
									/>
								)))}
							</ol>
						) : (
							<p className="item-comments__empty">No comments yet. Add the first one below.</p>
						)}
						{comments.hasMore ? (
							<Button
								className="item-comments__more"
								onClick={comments.loadMore}
								loading={comments.loadingMore}
							>
								{`Show ${Math.min(remaining, 50).toLocaleString()} more comment${Math.min(remaining, 50) === 1 ? '' : 's'}`}
							</Button>
						) : null}
						{comments.status === 'ready' && comments.error ? (
							<div className="item-comments__problem" role="alert">{comments.error}</div>
						) : null}
						{comments.canComment ? (
							<CommentTextBox
								key={normalizeId(itemId)}
								label="Add a comment"
								submitLabel="Post comment"
								hintVerb="Post"
								busy={comments.busy?.kind === 'add'}
								onSubmit={comments.add}
							/>
						) : null}
					</div>
				) : null}
			</DataState>
		</section>
	);
}
